'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useEffect, useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';

interface PushUser {
  identity: string;
  displayName: string;
}

function Tabs(props: React.PropsWithChildren<{}>) {
  const searchParams = useSearchParams();
  const requested = searchParams?.get('tab');
  const tabIndex = requested === 'custom' ? 2 : requested === 'call' ? 1 : 0;

  const router = useRouter();
  function onTabSelected(index: number) {
    const tab = index === 2 ? 'custom' : index === 1 ? 'call' : 'demo';
    router.push(`/?tab=${tab}`);
  }

  let tabs = React.Children.map(props.children, (child, index) => {
    return (
      <button
        className="lk-button"
        onClick={() => { onTabSelected(index); }}
        aria-pressed={tabIndex === index}
      >
        {/* @ts-ignore */}
        {child?.props.label}
      </button>
    );
  });

  return (
    <div className={styles.tabContainer}>
      <div className={styles.tabSelect}>{tabs}</div>
      {/* @ts-ignore */}
      {props.children[tabIndex]}
    </div>
  );
}

function DemoMeetingTab(props: { label: string }) {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const startMeeting = () => {
    if (e2ee) {
      router.push(`/rooms/${generateRoomId()}#${encodePassphrase(sharedPassphrase)}`);
    } else {
      router.push(`/rooms/${generateRoomId()}`);
    }
  };
  return (
    <div className={styles.tabContent}>
      <p style={{ margin: 0 }}>Crie uma sala de videoconferência da AM53 instantaneamente.</p>
      <button style={{ marginTop: '1rem' }} className="lk-button" onClick={startMeeting}>
        Iniciar reunião
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input id="use-e2ee" type="checkbox" checked={e2ee} onChange={(ev) => setE2ee(ev.target.checked)} />
          <label htmlFor="use-e2ee">Ativar criptografia ponta-a-ponta</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">Senha</label>
            <input id="passphrase" type="password" value={sharedPassphrase} onChange={(ev) => setSharedPassphrase(ev.target.value)} />
          </div>
        )}
      </div>
    </div>
  );
}

function CallSomeoneTab(props: { label: string }) {
  const router = useRouter();
  const [users, setUsers] = useState<PushUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [placing, setPlacing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [callerName, setCallerName] = useState<string>('');
  // Chamada em andamento — enquanto !== null exibimos a tela "chamando…".
  const [pendingCall, setPendingCall] = useState<{
    callId: string;
    roomName: string;
    targetName: string;
  } | null>(null);

  useEffect(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('mks.callerName') : null;
    if (stored) setCallerName(stored);
    void refresh();
  }, []);

  // Polling do estado da chamada: assim que o destinatário aceita no celular,
  // entramos direto na sala — sem passar pelo PreJoin.
  useEffect(() => {
    if (!pendingCall) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const startedAt = Date.now();
    const RING_TIMEOUT_MS = 60_000;
    const tick = async () => {
      try {
        const r = await fetch(`/_push/call/${pendingCall.callId}`);
        if (!cancelled && r.status === 404) {
          setError('A chamada expirou sem resposta.');
          setPendingCall(null);
          return;
        }
        const j = await r.json();
        if (cancelled) return;
        if (j.state === 'accepted') {
          const name = encodeURIComponent(callerName.trim());
          router.push(`/rooms/${pendingCall.roomName}?autojoin=1&name=${name}`);
          return;
        }
      } catch {
        // erro de rede transitório — tenta de novo no próximo tick
      }
      if (cancelled) return;
      if (Date.now() - startedAt > RING_TIMEOUT_MS) {
        setError('Ninguém atendeu a chamada.');
        setPendingCall(null);
        return;
      }
      timer = setTimeout(tick, 2000);
    };
    timer = setTimeout(tick, 2000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pendingCall, callerName, router]);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch('/_push/users');
      const j = await r.json();
      setUsers(Array.isArray(j.users) ? j.users : []);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  async function call(target: PushUser) {
    const name = callerName.trim();
    if (!name) {
      setError('Digite seu nome antes de ligar.');
      return;
    }
    localStorage.setItem('mks.callerName', name);
    setPlacing(target.identity);
    setError(null);
    try {
      const roomName = generateRoomId();
      const callerIdentity = 'web-' + name.toLowerCase().replace(/[^a-z0-9]/g, '-');
      const res = await fetch('/_push/call', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          callerIdentity,
          callerName: name,
          calleeIdentity: target.identity,
          roomName,
        }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`HTTP ${res.status}: ${t}`);
      }
      const j = await res.json();
      if (!j.callId) {
        throw new Error('resposta do servidor sem callId');
      }
      // Não entramos na sala ainda — exibimos "chamando…" e o polling
      // (useEffect acima) nos leva pra sala assim que o destinatário aceita.
      setPendingCall({ callId: j.callId, roomName, targetName: target.displayName });
    } catch (e) {
      setError(`Falha ao ligar: ${e}`);
    } finally {
      setPlacing(null);
    }
  }

  // Tela "chamando…" — substitui a lista enquanto a chamada toca.
  if (pendingCall) {
    return (
      <div className={styles.tabContent}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '2rem 0',
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: '#EF4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: 28,
              color: 'white',
            }}
          >
            {pendingCall.targetName.charAt(0).toUpperCase() || '?'}
          </div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            Chamando {pendingCall.targetName}…
          </div>
          <div style={{ opacity: 0.6, fontSize: 13, textAlign: 'center' }}>
            Aguardando a pessoa atender. Você entra na sala automaticamente.
          </div>
          <button
            type="button"
            className="lk-button"
            onClick={() => setPendingCall(null)}
            style={{ background: '#EF4444', marginTop: '0.5rem', paddingInline: '1.5rem' }}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <p style={{ margin: 0 }}>
        Liga pro celular de alguém registrado no app AM53 Meeting. O destinatário
        recebe uma chamada como se fosse uma ligação normal.
      </p>

      <label htmlFor="caller-name" style={{ marginTop: '0.5rem' }}>
        Seu nome
      </label>
      <input
        id="caller-name"
        type="text"
        placeholder="Como você quer aparecer"
        value={callerName}
        onChange={(ev) => setCallerName(ev.target.value)}
      />

      {loading ? (
        <p style={{ opacity: 0.6, margin: '0.5rem 0' }}>Carregando contatos…</p>
      ) : users.length === 0 ? (
        <p style={{ opacity: 0.6, margin: '0.5rem 0' }}>
          Nenhum contato registrado no AM53 Meeting ainda. Abre o app no celular e
          define um identity pra aparecer aqui.
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '0.5rem 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          {users.map((u) => (
            <li
              key={u.identity}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#EF4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                {u.displayName.charAt(0).toUpperCase() || '?'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{u.displayName}</div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>{u.identity}</div>
              </div>
              <button
                type="button"
                className="lk-button"
                disabled={placing !== null}
                onClick={() => call(u)}
                style={{ paddingInline: '0.9rem' }}
              >
                {placing === u.identity ? 'Ligando…' : 'Ligar'}
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={refresh}
        disabled={loading}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#EF4444',
          cursor: 'pointer',
          textDecoration: 'underline',
          fontSize: 12,
          padding: 0,
          alignSelf: 'flex-start',
        }}
      >
        Atualizar lista
      </button>

      {error && (
        <div
          style={{
            background: 'rgba(239,68,68,0.15)',
            border: '1px solid #EF4444',
            color: '#FCA5A5',
            padding: '0.5rem 0.75rem',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

function CustomConnectionTab(props: { label: string }) {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const serverUrl = formData.get('serverUrl');
    const token = formData.get('token');
    if (e2ee) {
      router.push(`/custom/?liveKitUrl=${serverUrl}&token=${token}#${encodePassphrase(sharedPassphrase)}`);
    } else {
      router.push(`/custom/?liveKitUrl=${serverUrl}&token=${token}`);
    }
  };
  return (
    <form className={styles.tabContent} onSubmit={onSubmit}>
      <p style={{ marginTop: 0 }}>Conecte em um servidor LiveKit personalizado usando URL + token.</p>
      <input id="serverUrl" name="serverUrl" type="url" placeholder="URL do servidor LiveKit: wss://..." required />
      <textarea id="token" name="token" placeholder="Token" required rows={5} style={{ padding: '1px 2px', fontSize: 'inherit', lineHeight: 'inherit' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input id="use-e2ee" type="checkbox" checked={e2ee} onChange={(ev) => setE2ee(ev.target.checked)} />
          <label htmlFor="use-e2ee">Ativar criptografia ponta-a-ponta</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">Senha</label>
            <input id="passphrase" type="password" value={sharedPassphrase} onChange={(ev) => setSharedPassphrase(ev.target.value)} />
          </div>
        )}
      </div>
      <hr style={{ width: '100%', borderColor: 'rgba(255, 255, 255, 0.15)', marginBlock: '1rem' }} />
      <button style={{ paddingInline: '1.25rem', width: '100%' }} className="lk-button" type="submit">Conectar</button>
    </form>
  );
}

export default function Page() {
  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className="header">
          <img
            src="/images/am53-logo.jpg"
            alt="AM53"
            style={{ maxHeight: '80px', width: 'auto', objectFit: 'contain' }}
          />
          <h2>Sala de videoconferência</h2>
        </div>
        <Suspense fallback="Carregando">
          <Tabs>
            <DemoMeetingTab label="Rápido" />
            <CallSomeoneTab label="Ligar para…" />
            <CustomConnectionTab label="Avançado" />
          </Tabs>
        </Suspense>
      </main>
      <footer data-lk-theme="default">
        AM53 · Powered by{' '}
        <a href="https://livekit.io" rel="noopener">LiveKit</a>
      </footer>
    </>
  );
}
